import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { combineLatest, Observable, Subscription, of } from 'rxjs';
import { datepickerValueToDate } from 'src/app/shared/functions/datepicker-value-to-date';
import {
  MainTableFilterSearchByModel,
  MainTableSortingType,
} from 'src/app/shared/models/main-table-filter.model';
import { IOption } from 'src/app/shared/models/option.model';

export enum MainTableFilterOperand {
  Equal = 'Equal',
  In = 'In',
  NameIn = 'NameIn',
  Contains = 'Contains',
  NameContains = 'NameContains',
  LessThan = 'LessThan',
  MoreThan = 'MoreThan',
  LessThanOrEqual = 'LessThanOrEqual',
  MoreThanOrEqual = 'MoreThanOrEqual',
  EqualDate = 'EqualDate',
  IsNull = 'IsNull',
}

export type MainTableFilterListOption = { label: string; value: string | boolean }[] | string[];

export interface MainTableFilterSectionData {
  labelKey: string;
  property: string;
  options: Observable<MainTableFilterListOption>;
  operand: string;
}

export interface MainTableFilterColumnData {
  operand: string;
  options: Observable<MainTableFilterListOption>;
  type: 'text' | 'number' | 'date' | 'boolean' | 'list';
  allowNullFilter?: boolean;
  skipTime?: boolean;
  sections?: MainTableFilterSectionData[];
}

export type MainTableSearchByValueType = string | (string | boolean)[] | number | boolean;

interface IOptionControl {
  value: string | boolean;
  label: string;
  control: FormControl;
}

interface IOptionSection {
  labelKey: string;
  property: string;
  operand: string;
  optionControls: IOptionControl[];
}

@Component({
  selector: 'app-main-table-column-filter',
  templateUrl: './main-table-column-filter.component.html',
  styleUrls: ['./main-table-column-filter.component.scss'],
  exportAs: 'appMainTableColumnFilter',
  standalone: false,
})
export class MainTableColumnFilterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() sortDirection: MainTableSortingType = '';
  @Input() column: string;
  @Input() sorting = false;
  @Input() initialFilters: MainTableFilterSearchByModel[] = [];
  @Input() filterConfig: Record<string, MainTableFilterColumnData> = {};
  @Output() filter = new EventEmitter<MainTableFilterSearchByModel[]>();
  @Output() close = new EventEmitter<void>();
  @Output() sort = new EventEmitter<Sort>();

  optionControlMap: IOptionControl[] = [];
  optionSections: IOptionSection[] = [];
  searchControl = new FormControl('');
  fromControl: FormControl<any> = new FormControl(null);
  toControl: FormControl<any> = new FormControl(null);
  dateFromControl: FormControl<any> = new FormControl(null);
  dateToControl: FormControl<any> = new FormControl(null);
  booleanControl: FormControl<any> = new FormControl(null);
  onlyNullControl: FormControl<any> = new FormControl(false);
  showOptions = false;
  showSearch = false;
  hasActiveFilters = false;
  columnData: MainTableFilterColumnData;
  booleanControlOptions: IOption[] = [];

  ascIcons: Record<string, string> = {
    number: 'arrow_upward',
    date: '',
    text: 'sort_by_alpha',
  };
  descIcons: Record<string, string> = {
    number: 'arrow_downward',
    date: '',
    text: 'sort_by_alpha',
  };
  ascLabels: Record<string, string> = {
    number: 'sortAscending',
    date: 'oldestFirst',
    text: 'sortAToZ',
    boolean: 'notCheckedFirst',
  };
  descLabels: Record<string, string> = {
    number: 'sortDecsending',
    date: 'latestFirst',
    text: 'sortZToA',
    boolean: 'checkedFirst',
  };

  private allOptionSections: IOptionSection[] = [];
  private initialized = false;
  private optionsSubscription = new Subscription();
  private rangeControlMapping: any = {
    number: { from: this.fromControl, to: this.toControl },
    date: { from: this.dateFromControl, to: this.dateToControl },
  };
  private subscriptions = new Subscription();

  get controlsValid(): boolean {
    return (
      this.fromControl.valid &&
      this.toControl.valid &&
      this.dateFromControl.valid &&
      this.dateToControl.valid &&
      this.searchControl.valid &&
      this.booleanControl.valid &&
      this.onlyNullControl.valid
    );
  }

  get columnType(): string {
    if (!this.columnData) {
      console.warn('No column data for column:', this.column);
      return 'text';
    }
    return this.columnData.type;
  }

  get isDateColumn(): boolean {
    return this.columnType === 'date';
  }

  get isNumberColumn(): boolean {
    return this.columnType === 'number';
  }

  get isTextColumn(): boolean {
    return this.columnType === 'text';
  }

  get isBooleanColumn(): boolean {
    return this.columnType === 'boolean';
  }

  get isSectionedList(): boolean {
    return !!this.columnData?.sections?.length;
  }

  ngOnInit(): void {
    this.refreshColumnData();
    this.hasActiveFilters = !!this.initialFilters.length;
    this.initControlsFromInitialFilters();
    this.initRangeSubscriptions();
    this.initNullDateSubscription();
    this.initSectionedSearchSubscription();
    this.fetchFilterOptions();
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterConfig || changes.column) {
      this.refreshColumnData();
      if (this.initialized) {
        this.resetOptionState();
        this.fetchFilterOptions();
        this.initControlsFromInitialFilters();
      }
    }

    if (changes.initialFilters && this.initialized) {
      this.hasActiveFilters = !!this.initialFilters.length;
      this.initControlsFromInitialFilters();
    }
  }

  ngOnDestroy(): void {
    this.optionsSubscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  initControlsFromInitialFilters(): void {
    this.hasActiveFilters = !!this.initialFilters.length;
    if (this.isSectionedList) {
      this.syncSectionControlsFromInitialFilters();
      this.searchControl.setValue('', { emitEvent: false });
      this.updateFilteredSections();
      return;
    }

    if (this.hasActiveFilters) {
      this.setRangeControlsFromInitialFilters();
      this.setTextControlsFromInitialFilters();
      this.booleanControl.setValue(this.initialFilters[0].value as unknown as boolean);
    } else {
      this.resetAllControls();
    }

    this.syncFlatOptionControlsFromInitialFilters();
  }

  fetchFilterOptions(): void {
    if (this.isSectionedList) {
      this.fetchSectionedFilterOptions();
      return;
    }

    const options = this.columnData.options ?? of([]);
    this.optionsSubscription.add(
      options.subscribe((opts) => {
        if (this.isBooleanColumn) {
          this.booleanControlOptions = opts.map((option: any) => {
            const isString = typeof option === 'string';
            const value = isString ? option : option.value;
            const label = isString ? option : option.label;
            return { id: value, name: label };
          });
          return;
        }

        this.optionControlMap = opts.map((option: any) => this.createOptionControl(option));
      })
    );
  }

  sortChange(direction: 'asc' | 'desc'): void {
    let currentDirection = '';
    if (this.sortDirection) {
      currentDirection =
        this.sortDirection === 'asc' || this.sortDirection === 'Ascending' ? 'asc' : 'desc';
    }

    const newDirection = currentDirection !== direction ? direction : '';
    this.sort.emit({ active: this.column, direction: newDirection });
    this.close.emit();
  }

  filterChange(): void {
    const filters = this.isSectionedList
      ? this.buildSectionedFilters()
      : this.buildFlatFilters(this.column);

    this.filter.emit(filters);
    this.hasActiveFilters = !!filters.length;
  }

  apply(): void {
    this.filterChange();
    this.cancel();
  }

  resetAllControls(): void {
    this.optionControlMap.forEach(({ control }) => control.reset());
    this.allOptionSections.forEach(({ optionControls }) => {
      optionControls.forEach(({ control }) => control.reset());
    });
    this.searchControl.reset();
    this.dateFromControl.reset();
    this.dateToControl.reset();
    this.fromControl.reset();
    this.toControl.reset();
    this.booleanControl.reset();
    this.onlyNullControl.reset();
    this.hasActiveFilters = false;
  }

  clear(): void {
    this.resetAllControls();
    if (this.sortDirection) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.sortChange(this.sortDirection);
    }
    this.filterChange();
    this.cancel();
  }

  cancel(): void {
    this.resetAllControls();
    this.close.emit();
  }

  private buildFlatFilters(filterKey: string): MainTableFilterSearchByModel[] {
    const filters: MainTableFilterSearchByModel[] = [];
    const selectedOptions = this.optionControlMap.filter(({ control }) => control.value);

    if (this.isBooleanColumn && this.booleanControl.value !== null) {
      filters.push(
        new MainTableFilterSearchByModel(
          filterKey,
          this.booleanControl.value,
          MainTableFilterOperand.Equal
        )
      );
    }

    if (selectedOptions.length) {
      const listOperand =
        this.columnData.operand === MainTableFilterOperand.NameIn
          ? MainTableFilterOperand.NameIn
          : MainTableFilterOperand.In;
      filters.push(
        new MainTableFilterSearchByModel(
          filterKey,
          selectedOptions.map(({ value }) => value),
          listOperand
        )
      );
    }

    if (this.showSearch && this.searchControl.value) {
      this.appendSearchFilter(filters, filterKey);
    }

    this.appendRangeFilters(filters, filterKey);
    return filters;
  }

  private buildSectionedFilters(): MainTableFilterSearchByModel[] {
    return this.allOptionSections.reduce(
      (filters: MainTableFilterSearchByModel[], section: IOptionSection) => {
        const selectedValues = section.optionControls
          .filter(({ control }) => control.value)
          .map(({ value }) => value as string);

        if (!selectedValues.length) {
          return filters;
        }

        filters.push(
          new MainTableFilterSearchByModel(section.property, selectedValues, section.operand)
        );
        return filters;
      },
      []
    );
  }

  private appendSearchFilter(filters: MainTableFilterSearchByModel[], filterKey: string): void {
    const searchValue = this.searchControl.value ?? '';

    if (this.isSectionedList) {
      return;
    }

    if (
      (this.columnData.operand === MainTableFilterOperand.In ||
        this.columnData.operand === MainTableFilterOperand.NameIn) &&
      filters[0]
    ) {
      (filters[0].value as (string | boolean)[]).push(searchValue);
      return;
    }

    filters.push(
      new MainTableFilterSearchByModel(
        filterKey,
        this.searchValueToFilterValue(searchValue),
        this.getTextSearchOperand()
      )
    );
  }

  private getTextSearchOperand(): MainTableFilterOperand {
    if (!this.isTextColumn) {
      return MainTableFilterOperand.Equal;
    }
    return this.columnData.operand === MainTableFilterOperand.NameContains
      ? MainTableFilterOperand.NameContains
      : MainTableFilterOperand.Contains;
  }

  private appendRangeFilters(filters: MainTableFilterSearchByModel[], filterKey: string): void {
    if (!(this.columnType in this.rangeControlMapping)) {
      return;
    }

    const fromControl = this.rangeControlMapping[this.columnType].from;
    const toControl = this.rangeControlMapping[this.columnType].to;

    if (!this.onlyNullControl.value && fromControl.value) {
      const value = this.getRangeFilterValue(fromControl.value);
      filters.push(
        new MainTableFilterSearchByModel(filterKey, value, MainTableFilterOperand.MoreThanOrEqual)
      );
    }

    if (!this.onlyNullControl.value && toControl.value) {
      const value = this.getRangeFilterValue(toControl.value, true);
      filters.push(
        new MainTableFilterSearchByModel(filterKey, value, MainTableFilterOperand.LessThanOrEqual)
      );
    }

    if (this.onlyNullControl.value) {
      filters.push(
        new MainTableFilterSearchByModel(filterKey, true, MainTableFilterOperand.IsNull)
      );
    }
  }

  private getRangeFilterValue(value: any, isToControl = false): string | number {
    let formattedValue = this.isDateColumn
      ? formatISO(
          isToControl
            ? endOfDay(datepickerValueToDate(value))
            : startOfDay(datepickerValueToDate(value))
        )
      : value;

    if (this.columnData.skipTime) {
      [formattedValue] = formattedValue.split('T');
    }

    return formattedValue;
  }

  private fetchSectionedFilterOptions(): void {
    const sectionConfigs = this.columnData.sections ?? [];
    const sectionOptions$ = sectionConfigs.map((section) => section.options ?? of([]));

    this.optionsSubscription.add(
      combineLatest(sectionOptions$).subscribe((sectionOptions) => {
        this.allOptionSections = sectionOptions.map((options, index) =>
          this.createSectionOptionState(sectionConfigs[index], options)
        );
        this.syncSectionControlsFromInitialFilters();
        this.updateFilteredSections();
      })
    );
  }

  private createSectionOptionState(
    section: MainTableFilterSectionData,
    options: MainTableFilterListOption
  ): IOptionSection {
    return {
      labelKey: section.labelKey,
      property: section.property,
      operand: section.operand,
      optionControls: options.map((option: any) =>
        this.createOptionControl(option, section.property)
      ),
    };
  }

  private createOptionControl(option: any, property?: string): IOptionControl {
    const isString = typeof option === 'string';
    const value = isString ? option : option.value;
    const label = String(isString ? option : (option.label ?? value));
    const control = new FormControl(this.isValueSelected(property ?? this.column, value));

    return { value, label, control };
  }

  private isValueSelected(property: string, value: string | boolean): boolean {
    return this.initialFilters.some((filter) => {
      if (filter.property !== property) {
        return false;
      }

      return (
        filter.value === value || (Array.isArray(filter.value) && filter.value.includes(value))
      );
    });
  }

  private updateFilteredSections(): void {
    const searchTerm = (this.searchControl.value || '').toLowerCase().trim();

    if (!searchTerm) {
      this.optionSections = this.allOptionSections.filter(
        ({ optionControls }) => optionControls.length > 0
      );
      return;
    }

    this.optionSections = this.allOptionSections
      .map((section) => ({
        ...section,
        optionControls: section.optionControls.filter((option) =>
          this.shouldDisplaySectionOption(option, searchTerm)
        ),
      }))
      .filter(({ optionControls }) => optionControls.length > 0);
  }

  private shouldDisplaySectionOption(option: IOptionControl, searchTerm: string): boolean {
    if (option.control.value) {
      return true;
    }

    const searchableText = `${option.label ?? ''} ${option.value ?? ''}`.toLowerCase();
    return searchableText.includes(searchTerm);
  }

  private syncSectionControlsFromInitialFilters(): void {
    this.allOptionSections.forEach((section) => {
      section.optionControls.forEach((option) => {
        option.control.setValue(this.isValueSelected(section.property, option.value), {
          emitEvent: false,
        });
      });
    });
  }

  private syncFlatOptionControlsFromInitialFilters(): void {
    this.optionControlMap.forEach((option) => {
      option.control.setValue(this.isValueSelected(this.column, option.value), {
        emitEvent: false,
      });
    });
  }

  private setRangeControlsFromInitialFilters(): void {
    if (!(this.columnType in this.rangeControlMapping)) {
      return;
    }

    this.initialFilters.forEach((filter) => {
      if (filter.operand === MainTableFilterOperand.MoreThanOrEqual) {
        this.rangeControlMapping[this.columnData.type].from.setValue(filter.value);
      }

      if (filter.operand === MainTableFilterOperand.LessThanOrEqual) {
        this.rangeControlMapping[this.columnData.type].to.setValue(filter.value);
      }

      this.onlyNullControl.setValue(
        this.columnData.allowNullFilter && filter.operand === MainTableFilterOperand.IsNull
      );
    });
  }

  private setTextControlsFromInitialFilters(): void {
    if (
      this.isTextColumn &&
      (this.columnData.operand === MainTableFilterOperand.Contains ||
        this.columnData.operand === MainTableFilterOperand.NameContains)
    ) {
      this.searchControl.setValue(this.initialFilters[0].value as string);
    }
  }

  private refreshColumnData(): void {
    this.columnData = this.filterConfig[this.column] ?? this.createFallbackColumnData();
    this.showSearch =
      this.isSectionedList ||
      (this.columnData.operand !== MainTableFilterOperand.In &&
        this.columnData.operand !== MainTableFilterOperand.NameIn);
  }

  private resetOptionState(): void {
    this.optionsSubscription.unsubscribe();
    this.optionsSubscription = new Subscription();
    this.optionControlMap = [];
    this.optionSections = [];
    this.allOptionSections = [];
    this.booleanControlOptions = [];
  }

  private initRangeSubscriptions(): void {
    if (this.isNumberColumn) {
      this.subscriptions.add(this.fromControl.valueChanges.subscribe(() => this.validateRange()));
      this.subscriptions.add(this.toControl.valueChanges.subscribe(() => this.validateRange()));
    }

    if (this.isDateColumn) {
      this.subscriptions.add(
        this.dateFromControl.valueChanges.subscribe(() => this.validateDateRange())
      );
      this.subscriptions.add(
        this.dateToControl.valueChanges.subscribe(() => this.validateDateRange())
      );
    }
  }

  private initNullDateSubscription(): void {
    if (!this.isDateColumn || !this.columnData.allowNullFilter) {
      return;
    }

    this.subscriptions.add(
      this.onlyNullControl.valueChanges.subscribe(() => {
        if (this.onlyNullControl.value) {
          this.dateFromControl.disable();
          this.dateToControl.disable();
          return;
        }

        this.dateFromControl.enable();
        this.dateToControl.enable();
      })
    );
  }

  private initSectionedSearchSubscription(): void {
    if (!this.isSectionedList) {
      return;
    }

    this.subscriptions.add(
      this.searchControl.valueChanges.subscribe(() => this.updateFilteredSections())
    );
  }

  private validateRange(): void {
    if (this.fromControl.value == null || this.toControl.value == null) {
      this.resetNumericControlsErrors();
      return;
    }

    const from = +this.fromControl.value;
    const to = +this.toControl.value;
    if (from > to || to < from) {
      this.fromControl.setErrors({ rangeInvalid: true });
      this.toControl.setErrors({ rangeInvalid: true });
      return;
    }

    this.resetNumericControlsErrors();
  }

  private validateDateRange(): void {
    if (this.dateFromControl.value == null || this.dateToControl.value == null) {
      this.resetDateControlsErrors();
      return;
    }

    const fromDate = datepickerValueToDate(this.dateFromControl.value);
    const toDate = datepickerValueToDate(this.dateToControl.value);
    if (new Date(fromDate) > new Date(toDate) || new Date(toDate) < new Date(fromDate)) {
      this.dateFromControl.setErrors({ rangeInvalid: true });
      this.dateToControl.setErrors({ rangeInvalid: true });
      return;
    }

    this.resetDateControlsErrors();
  }

  private resetNumericControlsErrors(): void {
    this.fromControl.setErrors(null);
    this.toControl.setErrors(null);
  }

  private resetDateControlsErrors(): void {
    this.dateFromControl.setErrors(null);
    this.dateToControl.setErrors(null);
  }

  private searchValueToFilterValue(value: string): MainTableSearchByValueType {
    if (this.columnData.type === 'number') {
      return +value;
    }

    return this.columnData.operand === MainTableFilterOperand.In ||
      this.columnData.operand === MainTableFilterOperand.NameIn
      ? [value]
      : value;
  }

  private createFallbackColumnData(): MainTableFilterColumnData {
    console.warn('No column data for column:', this.column);
    return {
      operand: MainTableFilterOperand.Equal,
      options: of([]),
      type: 'text',
    };
  }
}
